import { FetchResult } from 'apollo-boost';
import { mapValues } from 'lodash';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import {
    NoticeType,
    ResetGridData,
    resetGridData,
    showNoticeMessage
} from '../../actions/grid-screen';
import {
    ADD_SMART_FILTER,
    AddSmartFilterAction,
    setSmartFilterValidationErrors,
    SetSmartFilterValidationErrorsAction,
    UPDATE_SMART_FILTER,
    UPDATE_SMART_FILTER_SUCCESS,
    updateFiltersError,
    UpdateSmartFilterAction,
    updateSmartFilterSuccess,
    UpdateSmartFilterSuccessAction
} from '../../actions/groupFilterInspector';
import {
    DELETE_GROUP_FILTER,
    DeleteGroupFilterAction,
    GET_GROUP_FILTERS,
    getGroupFilters,
    GetGroupFiltersAction,
    setGroupFilters
} from '../../actions/groupsEdit';
import {
    getFiltersSaveValidationResult,
    getSmartFiltersData,
    mapFeedFilterDataToAddApiFormat,
    mapFeedFilterDataToUpdateApiFormat
} from '../../mappers/group/group';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { FeedFiltersModalShownView } from '../../service/FeedFiltersHelpers/FeedFiltersHelpers';
import {
    defineTreeTabValidationErrors,
    mapValidationResultItemsToFieldsFilter
} from '../../service/GroupFiltersErrors/GroupFiltersErrors';
import { EpicDeps } from '../../service/StoreFactory';
import {
    AddSmartFilters,
    GdAddSmartFilterConditionInput,
    GdSmartFilterTab,
    GdUpdateSmartFilterConditionInput,
    SmartFiltersQuery,
    UpdateSmartFilters
} from '../../typings/graphql-grid';
import { GroupId } from '../../typings/groups';
import { EditGroupData } from '../../typings/groupsEditState';
import { State } from '../../typings/state';

interface UpdateSmartFilterPipedData {
    login: string;
    adGroupId: GroupId;
    campaignsIds: Array<string>;
    groups: Record<GroupId, EditGroupData>;
}

const updateSmartFilterSuccessEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = action$ => {
    return action$.pipe(
        ofType(UPDATE_SMART_FILTER_SUCCESS),
        mergeMap(
            (
                action: UpdateSmartFilterSuccessAction
            ): Observable<GetGroupFiltersAction | ResetGridData> => {
                switch (action.view) {
                    case FeedFiltersModalShownView.filtersGrid:
                        return of(resetGridData());
                    case FeedFiltersModalShownView.groupsEdit:
                        return of(getGroupFilters(action.groupId));
                    default:
                        return EMPTY;
                }
            }
        )
    );
};

const validateFilters = (
    result: FetchResult<AddSmartFilters | UpdateSmartFilters>,
    conditions: Array<
        GdUpdateSmartFilterConditionInput | GdAddSmartFilterConditionInput
    >,
    tab: GdSmartFilterTab,
    view: FeedFiltersModalShownView,
    groupId: string
): Observable<
    UpdateSmartFilterSuccessAction | SetSmartFilterValidationErrorsAction
> => {
    const rawValidationErrors = getFiltersSaveValidationResult(result);
    if (rawValidationErrors.length === 0) {
        return of(updateSmartFilterSuccess(view, groupId));
    }

    const validationErrors = mapValidationResultItemsToFieldsFilter(
        rawValidationErrors,
        tab
    );

    return of(
        setSmartFilterValidationErrors(
            defineTreeTabValidationErrors(validationErrors, conditions, tab)
        )
    );
};

const handleUpdateFilterError = (err: ApiMethodExecutingError) => {
    if (!(err instanceof NotSyncedError)) {
        showNoticeMessage(NoticeType.error, err.getMessage());
    }

    return of(updateFiltersError(err));
};

const getGroupFiltersEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(GET_GROUP_FILTERS),
        mergeMap(() => {
            const { commonData, editGroups } = store$.value;
            const { login } = commonData;
            const {
                groupFilterInspector: { adGroupId },
                campaignsIds,
                groups
            } = editGroups;

            if (!adGroupId) {
                return EMPTY;
            }

            const pipedData = {
                login,
                adGroupId,
                campaignsIds,
                groups
            };

            return forkJoin([
                deps.smartFiltersDataProvider.getSmartFilters(
                    campaignsIds,
                    login,
                    [adGroupId]
                ),
                of(pipedData)
            ]);
        }),
        mergeMap(
            ([result, pipedData]: [
                FetchResult<SmartFiltersQuery>,
                UpdateSmartFilterPipedData
            ]) => {
                const { adGroupId, groups } = pipedData;
                let filters = {};
                if (result.data) {
                    filters = getSmartFiltersData(
                        result.data.client.smartFilters.rowset,
                        mapValues(groups, group => ({
                            strategy: group.campaign.strategyType,
                            platform: group.campaign.platform
                        }))
                    );
                }

                return of(setGroupFilters(adGroupId, filters[adGroupId]));
            }
        )
    );
};

const addSmartFilterEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ADD_SMART_FILTER),
        mergeMap((action: AddSmartFilterAction) => {
            const { commonData, editGroups } = store$.value;
            const { login } = commonData;
            const {
                groups,
                groupFilterInspector: { filter, adGroupId, schema }
            } = editGroups;

            if (!adGroupId) {
                return EMPTY;
            }

            const query = mapFeedFilterDataToAddApiFormat(
                filter,
                adGroupId,
                schema,
                groups[adGroupId].campaign.strategyType
            );

            return from(
                deps.smartFiltersMutationExecutor.addSmartFilters(query, login)
            ).pipe(
                mergeMap(result =>
                    validateFilters(
                        result,
                        query.addItems[0].conditions,
                        filter.tab,
                        action.view,
                        adGroupId
                    )
                ),
                catchError(handleUpdateFilterError)
            );
        })
    );
};

const updateSmartFilterEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(UPDATE_SMART_FILTER),
        mergeMap((action: UpdateSmartFilterAction) => {
            const { commonData, editGroups } = store$.value;
            const { login } = commonData;
            const {
                groupFilterInspector: { filter, adGroupId, schema }
            } = editGroups;

            if (!adGroupId) {
                return EMPTY;
            }

            const query = mapFeedFilterDataToUpdateApiFormat(
                filter,
                schema,
                filter.campaign.strategy
            );

            const conditions = query.updateItems[0].conditions || [];

            return from(
                deps.smartFiltersMutationExecutor.updateSmartFilters(
                    query,
                    login
                )
            ).pipe(
                mergeMap(result =>
                    validateFilters(
                        result,
                        conditions,
                        filter.tab,
                        action.view,
                        adGroupId
                    )
                ),
                catchError(handleUpdateFilterError)
            );
        })
    );
};

const deleteSmartFilterEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(DELETE_GROUP_FILTER),
        mergeMap(({ filterId }: DeleteGroupFilterAction) => {
            const { login } = store$.value.commonData;
            return from(
                deps.smartFiltersMutationExecutor.deleteSmartFilters(
                    {
                        deleteItems: [{ id: filterId }]
                    },
                    login
                )
            );
        }),
        mergeMap(() => {
            return EMPTY;
        })
    );
};

export {
    addSmartFilterEpic,
    deleteSmartFilterEpic,
    updateSmartFilterEpic,
    getGroupFiltersEpic,
    updateSmartFilterSuccessEpic
};
